import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Droplets, Save, Plus, Trash2, Calculator } from 'lucide-react';
import { formatDateTime } from '../lib/datetime';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatLKR = (n) => `LKR ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Preview the tariff for a given usage using the same cumulative-upper interpretation
 * as the backend, so admins can double-check before saving.
 */
const previewCharge = (usage, slabs) => {
  usage = Math.max(0, Number(usage) || 0);
  const ordered = [...slabs].sort((a, b) => (a.max_units == null ? 1e12 : Number(a.max_units)) - (b.max_units == null ? 1e12 : Number(b.max_units)));
  let usageCharge = 0;
  let topSlab = ordered[0];
  let prevUpper = 0;
  const rows = [];
  for (const slab of ordered) {
    const upper = slab.max_units == null ? Infinity : Number(slab.max_units);
    if (usage <= prevUpper) break;
    const unitsHere = Math.min(usage, upper) - prevUpper;
    if (unitsHere > 0) {
      const charge = unitsHere * Number(slab.rate_per_unit || 0);
      usageCharge += charge;
      rows.push({ ...slab, units: unitsHere, charge });
      topSlab = slab;
    }
    prevUpper = upper;
  }
  const service = topSlab ? Number(topSlab.monthly_service_charge || 0) : 0;
  return { usageCharge, service, total: usageCharge + service, rows, topSlab };
};

export const WaterTariff = () => {
  const [label, setLabel] = useState('');
  const [slabs, setSlabs] = useState([]);
  const [vatPercent, setVatPercent] = useState(18);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUnits, setPreviewUnits] = useState(22);
  // Client-side stable ids so react keys survive add/remove/reorder of slabs
  // (index-based keys swap input focus between rows on removal).
  const nextSlabId = useRef(1);
  const withUid = (s) => ({ ...s, _uid: nextSlabId.current++ });

  // Load once on mount — `load` is a stable function scoped to this component
  // and only touches local state, so re-running is not needed and would risk
  // clobbering unsaved edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/water-tariff`, { withCredentials: true });
      setConfigured(data.configured !== false);
      setLabel(data.label || '');
      nextSlabId.current = 1;
      setSlabs((data.slabs || []).map(withUid));
      setVatPercent(data.vat_percent ?? 18);
      setUpdatedAt(data.updated_at);
    } catch (err) {
      console.error('WaterTariff: failed to load', err);
      toast.error('Failed to load tariff');
    } finally {
      setLoading(false);
    }
  };

  const updateSlab = (idx, field, value) => {
    const next = [...slabs];
    if (field === 'max_units') {
      next[idx].max_units = value === '' || value == null ? null : Number(value);
    } else {
      next[idx][field] = Number(value);
    }
    setSlabs(next);
  };

  const addSlab = () => {
    const last = slabs[slabs.length - 1];
    const lastUpper = last?.max_units != null ? Number(last.max_units) : 0;
    setSlabs([...slabs, withUid({ min_units: lastUpper + 1, max_units: lastUpper + 10, rate_per_unit: 0, monthly_service_charge: 0 })]);
  };

  const removeSlab = (idx) => {
    if (slabs.length <= 1) { toast.error('At least one slab is required'); return; }
    setSlabs(slabs.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    try {
      const vatNum = Number(vatPercent);
      if (isNaN(vatNum) || vatNum < 0 || vatNum > 100) {
        toast.error('VAT % must be between 0 and 100');
        setSaving(false);
        return;
      }
      if (!slabs.length) {
        toast.error('Add at least one slab before saving.');
        setSaving(false);
        return;
      }
      const payload = {
        label,
        vat_percent: vatNum,
        slabs: slabs.map(s => ({
          min_units: Number(s.min_units),
          max_units: s.max_units == null || s.max_units === '' ? null : Number(s.max_units),
          rate_per_unit: Number(s.rate_per_unit),
          monthly_service_charge: Number(s.monthly_service_charge),
        })),
      };
      const { data } = await axios.put(`${API}/water-tariff`, payload, { withCredentials: true });
      setUpdatedAt(data.updated_at);
      setVatPercent(data.vat_percent ?? vatNum);
      setConfigured(true);
      toast.success('Water tariff saved. New invoices will use these rates.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tariff');
    } finally {
      setSaving(false);
    }
  };

  const preview = previewCharge(previewUnits, slabs);
  const vatNumSafe = Math.max(0, Number(vatPercent) || 0);
  const previewVat = Number(((preview.total * vatNumSafe) / 100).toFixed(2));
  const previewGrand = Number((preview.total + previewVat).toFixed(2));

  return (
    <div className="space-y-6" data-testid="water-tariff-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>Water Tariff</h1>
          <p className="text-sm text-[#64748b]">
            Slab-based tariff applied to every resident's monthly water invoice. Charges progress across slabs; service charge follows the topmost slab reached.
          </p>
          {updatedAt
            ? <p className="text-xs text-[#94a3b8] mt-1">Last updated {formatDateTime(updatedAt)}</p>
            : !loading && <p className="text-xs text-amber-600 mt-1">Not configured yet — set your slabs and click Save Tariff.</p>}
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving || loading} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="save-tariff-button">
            <Save className="mr-2 h-4 w-4" /> {saving ? 'Saving…' : 'Save Tariff'}
          </Button>
        </div>
      </div>

      {!loading && !configured && slabs.length === 0 && (
        <Card className="p-6 bg-amber-50 border border-amber-200" data-testid="tariff-empty-state">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Droplets className="h-5 w-5 text-amber-700" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-amber-900" style={{ fontFamily: 'Outfit' }}>No water tariff configured</h3>
              <p className="text-sm text-amber-800 mt-1">
                Water invoices can't be generated until you set the slab tariff for your building. Click <strong>Add Slab</strong> below to enter your own rates, then click <strong>Save Tariff</strong>.
              </p>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6 bg-white border border-[#e2e8f0]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Tariff Label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g., NWSDB Tariff Table 01" data-testid="tariff-label-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">VAT % (applied on subtotal)</Label>
            <div className="relative">
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={vatPercent}
                onChange={(e) => setVatPercent(e.target.value)}
                placeholder="18"
                className="pr-8"
                data-testid="tariff-vat-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#64748b]">%</span>
            </div>
            <p className="text-xs text-[#94a3b8]">Charged on every resident's monthly water bill (Usage + Service Charge).</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
              <tr>
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-widest text-[#64748b]">From (units)</th>
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-widest text-[#64748b]">To (units)</th>
                <th className="text-right p-3 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Rate / Unit (LKR)</th>
                <th className="text-right p-3 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Monthly Service (LKR)</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="p-6 text-center text-sm text-[#64748b]">Loading…</td></tr>
              ) : slabs.length === 0 ? (
                <tr><td colSpan={5} className="p-6 text-center text-sm text-[#64748b]">No slabs yet. Click <strong>Add Slab</strong> below to get started.</td></tr>
              ) : slabs.map((s, i) => (
                <tr key={s._uid || `slab-${i}`} className="border-b border-[#f1f5f9] last:border-0" data-testid={`slab-row-${i}`}>
                  <td className="p-2">
                    <Input type="number" step="1" min="0" value={s.min_units} onChange={(e) => updateSlab(i, 'min_units', e.target.value)} className="h-9 w-24" data-testid={`slab-${i}-min`} />
                  </td>
                  <td className="p-2">
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      value={s.max_units == null ? '' : s.max_units}
                      onChange={(e) => updateSlab(i, 'max_units', e.target.value)}
                      placeholder="∞"
                      className="h-9 w-24"
                      data-testid={`slab-${i}-max`}
                    />
                  </td>
                  <td className="p-2 text-right">
                    <Input type="number" step="0.01" min="0" value={s.rate_per_unit} onChange={(e) => updateSlab(i, 'rate_per_unit', e.target.value)} className="h-9 w-28 ml-auto text-right" data-testid={`slab-${i}-rate`} />
                  </td>
                  <td className="p-2 text-right">
                    <Input type="number" step="0.01" min="0" value={s.monthly_service_charge} onChange={(e) => updateSlab(i, 'monthly_service_charge', e.target.value)} className="h-9 w-28 ml-auto text-right" data-testid={`slab-${i}-service`} />
                  </td>
                  <td className="p-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => removeSlab(i)} className="text-red-600 hover:bg-red-50 h-9 w-9 p-0" data-testid={`remove-slab-${i}`} title="Remove slab">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <Button variant="outline" onClick={addSlab} className="border-[#e2e8f0]" data-testid="add-slab-button">
            <Plus className="mr-2 h-4 w-4" /> Add Slab
          </Button>
          <p className="text-xs text-[#64748b] mt-2">Leave the last slab's "To" empty for the "over N" tier (unlimited). Slab widths cover units {'>'} previous "To" up to and including this "To".</p>
        </div>
      </Card>

      {/* Live preview */}
      <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid="tariff-preview-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-md bg-cyan-100 flex items-center justify-center">
            <Calculator className="h-5 w-5 text-cyan-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Live Preview</h3>
            <p className="text-xs text-[#64748b]">Try any usage figure to see how the tariff applies before saving.</p>
          </div>
        </div>
        <div className="flex items-end gap-4 flex-wrap mb-4">
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Sample Usage (units)</Label>
            <Input type="number" step="1" min="0" value={previewUnits} onChange={(e) => setPreviewUnits(e.target.value)} className="w-40 h-10" data-testid="preview-units-input" />
          </div>
          <div className="ml-auto text-right">
            <p className="text-xs uppercase tracking-widest text-[#64748b]">Total (incl. VAT)</p>
            <p className="text-2xl font-semibold text-cyan-700" style={{ fontFamily: 'Outfit' }} data-testid="preview-total">{formatLKR(previewGrand)}</p>
            <p className="text-xs text-[#64748b]">
              Usage {formatLKR(preview.usageCharge)} · Service {formatLKR(preview.service)} · VAT@{vatNumSafe}% {formatLKR(previewVat)}
            </p>
          </div>
        </div>
        {preview.rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-[#64748b]">
                <tr>
                  <th className="text-left p-2">Slab</th>
                  <th className="text-right p-2">Units</th>
                  <th className="text-right p-2">Rate</th>
                  <th className="text-right p-2">Charge</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, i) => (
                  <tr key={`preview-${row.min_units}-${row.max_units ?? 'inf'}-${i}`} className="border-t border-[#f1f5f9]">
                    <td className="p-2 text-[#64748b]">{row.min_units}–{row.max_units ?? '∞'}</td>
                    <td className="p-2 text-right">{row.units}</td>
                    <td className="p-2 text-right">LKR {row.rate_per_unit}</td>
                    <td className="p-2 text-right font-medium">{formatLKR(row.charge)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="text-sm">
                <tr className="border-t border-[#e2e8f0]">
                  <td colSpan={3} className="p-2 text-right text-[#64748b]">Monthly service charge</td>
                  <td className="p-2 text-right font-medium">{formatLKR(preview.service)}</td>
                </tr>
                <tr>
                  <td colSpan={3} className="p-2 text-right text-[#64748b]">Subtotal</td>
                  <td className="p-2 text-right font-medium">{formatLKR(preview.total)}</td>
                </tr>
                <tr>
                  <td colSpan={3} className="p-2 text-right text-[#64748b]">VAT @ {vatNumSafe}%</td>
                  <td className="p-2 text-right font-medium" data-testid="preview-vat-amount">{formatLKR(previewVat)}</td>
                </tr>
                <tr className="border-t border-cyan-200 bg-cyan-50/40">
                  <td colSpan={3} className="p-2 text-right font-semibold text-cyan-800">Total (incl. VAT)</td>
                  <td className="p-2 text-right font-semibold text-cyan-800" data-testid="preview-grand-total">{formatLKR(previewGrand)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default WaterTariff;
